import React from "react";
import { shallowEqual, useSelector } from "react-redux";
import { Link as RouterLink } from "react-router";
import AddIcon from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import tokens from "@/theme/tokens";

import socialFacebook from "@/images/social_facebook.png";
import socialTwitter from "@/images/social_twitter.png";
import socialLinkedin from "@/images/social_linkedin.png";
import {
  railSectionSx,
  socialHandleSx,
  socialIconSx,
  socialLinkButtonSx,
  socialListSx,
} from "../styles";

interface SocialLinksProps {
  isUsersProfile: boolean;
}

type SocialLinkValues = {
  linkedin: string;
  twitter: string;
  facebook: string;
};

const NETWORKS = [
  { key: "linkedin", icon: socialLinkedin, label: "LinkedIn", url: "https://www.linkedin.com/in/" },
  { key: "twitter", icon: socialTwitter, label: "Twitter", url: "https://www.twitter.com/" },
  { key: "facebook", icon: socialFacebook, label: "Facebook", url: "https://www.facebook.com/" },
] as const;

/** The rail's section heading, with an optional action to its right. */
const RailHeading = ({
  title,
  action,
}: {
  title: string;
  action?: React.ReactNode;
}) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
  >
    <Typography variant="h3" sx={{ color: "common.white" }}>
      {title}
    </Typography>
    {action}
  </Stack>
);

/**
 * Read-only on this page for both the owner and visitors — editing moved to
 * the "Social Links" card on Account Settings' General tab
 * (`Pages/Settings/components/SocialLinks.tsx`). What used to switch this
 * component into an edit form now links out to it instead.
 */
const SocialLinks = ({ isUsersProfile }: SocialLinksProps) => {
  const { t } = useTranslation(["translation.Profile"]);

  const links = useSelector(
    (state: any) =>
      (isUsersProfile
        ? state.profile.socialLinks
        : state.browse.browseProfile.get("socialLinks")
      ).toJS() as Partial<SocialLinkValues>,
    shallowEqual
  );
  const socialLinksLoaded = useSelector((state: any) =>
    isUsersProfile
      ? Boolean(state.profile.pageMetaData.get("socialLinksLoaded"))
      : true
  );

  const hasSocialLinks = Boolean(
    links.twitter || links.facebook || links.linkedin
  );

  const openSocialLink = (linkAddress: string, socialType: string) => {
    if (window.confirm(`Open in ${socialType}?`)) {
      window.open(linkAddress, "_blank");
    }
  };

  const editLink = (
    <IconButton
      component={RouterLink}
      to="/me/settings"
      aria-label={t("Edit")}
      size="small"
      sx={{ color: tokens.color.accentTeal }}
    >
      <EditOutlinedIcon fontSize="small" />
    </IconButton>
  );

  if (!socialLinksLoaded) {
    return (
      <Box sx={railSectionSx}>
        <RailHeading title={t("SocialLinks")} />
        <Typography variant="body2" sx={{ color: "grey.400" }}>
          {t("loading")}
        </Typography>
      </Box>
    );
  }

  if (hasSocialLinks) {
    return (
      <Box sx={railSectionSx}>
        <RailHeading
          title={t("Connect")}
          action={isUsersProfile ? editLink : undefined}
        />
        <Box sx={socialListSx}>
          {NETWORKS.filter((network) => links[network.key]).map((network) => (
            <Button
              key={network.key}
              onClick={() =>
                openSocialLink(
                  `${network.url}${links[network.key]}`,
                  network.label
                )
              }
              size="small"
              startIcon={
                <Box sx={socialIconSx}>
                  <img src={network.icon} alt={network.key} />
                </Box>
              }
              sx={socialLinkButtonSx}
            >
              <Box component="span" sx={socialHandleSx}>
                {links[network.key]}
              </Box>
            </Button>
          ))}
        </Box>
      </Box>
    );
  }

  if (!isUsersProfile) return null;

  return (
    <Box sx={railSectionSx}>
      <RailHeading title={t("SocialLinks")} />
      <Stack spacing={1} sx={{ alignItems: "flex-start" }}>
        <Typography variant="body2" sx={{ color: "common.white" }}>
          {t("ProfilePhrase2")}
        </Typography>
        <Button
          component={RouterLink}
          to="/me/settings"
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          sx={{ ...socialLinkButtonSx, color: tokens.color.accentTeal }}
        >
          {t("AddSocialLinks")}
        </Button>
      </Stack>
    </Box>
  );
};

export default SocialLinks;
